import type { ColumnDescriptor, FilterSpec } from './types'

/**
 * Быстрые фильтры списка и прочие настройки экрана по сущности (дизайн: тулбар списка).
 *
 * ## Чип — это фильтр механизма списков, а не отдельный запрос
 *
 * Чип раскрывается в обычные условия `FilterSpec` и уходит тем же телом страницы, что и
 * условия конструктора: сервер сужает предикатом по каталогу колонок, и чип не может
 * показать то, чего не покажет фильтр, набранный руками. Свой эндпоинт под «Мои» или
 * «Просроченные» дал бы второй ответ на тот же вопрос, и они разошлись бы.
 *
 * ## Чип рисуется, только если сервер его примет
 *
 * Каждый чип объявляет колонки, на которые опирается, и показывается, только когда все они
 * пришли в описании списка и фильтруемы (`availableChips`). Колонка за полевым правом до
 * клиента не доезжает — и чип по ней не рисуется, а не отказывает 422 при нажатии.
 *
 * ## Чего здесь нет и почему
 *
 * Макет предлагает «Без задачи» и «Застрявшие» у сделок, «Ниже минимума» и «Нет
 * свободного» у остатков. В каталоге списков нет колонок, по которым сервер мог бы это
 * отфильтровать (задачи — другая таблица, застой считает домен, минимума у остатка нет,
 * «свободно» — разность двух колонок). Чип, который фильтрует на клиенте загруженную
 * страницу, врал бы ровно так же, как клиентская сортировка (см. DataTable), поэтому их
 * нет, пока сервер их не умеет.
 *
 * Счётчиков на чипах тоже нет: каждый — `count(*)` с полным проходом на каждое открытие
 * списка, то есть ровно та цена, ради избавления от которой пагинация сделана курсорной.
 */

export interface QuickContext {
  /** Сотрудник текущей учётной записи; у учётной записи без карточки сотрудника — null. */
  employeeId: string | null
  /**
   * Учётная запись (#123): исполнитель обращения — учётная запись, а не сотрудник, и «Мои»
   * у обращений сравнивается с ней. Необязательно: экраны без такого чипа его не передают.
   */
  userId?: string | null
  /** Сегодня, `YYYY-MM-DD` в местном времени — граница «просрочено». */
  today: string
}

export interface QuickFilter {
  key: string
  /** Ключ перевода подписи чипа. */
  labelKey: string
  /** Колонки, на которые чип опирается: без любой из них чип не показывается. */
  columns: string[]
  /** Условия чипа; null — чип неприменим к этому пользователю (нет карточки сотрудника). */
  build: (context: QuickContext) => FilterSpec[] | null
}

export interface DerivedColumn {
  /** Имя виртуальной колонки — только на клиенте, на сервер не уходит никогда. */
  name: string
  /** Вставляется сразу после этой колонки; без неё виртуальная не показывается. */
  after: string
  /** Колонки строки, из которых она считается; все должны быть в ответе страницы. */
  from: string[]
  compute: (row: Record<string, unknown>) => number | null
  /**
   * Ключ перевода пометки, когда значение не больше нуля. Ноль не прячется (дизайн: «ноль
   * показан как 0 с пометкой») — пустая ячейка выглядела бы незаполненной.
   */
  flagWhenNotPositive?: string
}

export interface EntityScreen {
  quick?: QuickFilter[]
  /** Колонка поиска по списку (`contains`); по умолчанию — первая подходящая из SEARCH_FALLBACK. */
  search?: string | null
  /** Группировка, с которой список открывается, если колонка есть в описании. */
  defaultGroupBy?: string
  /** Колонки, которые сверяют глазами с этикеткой: моноширинным. */
  mono?: string[]
  /** Колонка, выделенная жирным — главная цифра экрана. */
  strong?: string[]
  derived?: DerivedColumn[]
}

const mine = (column: string): QuickFilter => ({
  key: 'mine',
  labelKey: 'quick.mine',
  columns: [column],
  build: ({ employeeId }) =>
    employeeId ? [{ column, operator: 'eq', value: employeeId }] : null,
})

/**
 * «Свободно» = количество − резерв.
 *
 * Считается на клиенте, потому что в каталоге такой колонки нет; поэтому она не
 * сортируется и не фильтруется — сортировка по ней сортировала бы страницу, а не склад.
 * Пустое любое из слагаемых — пусто, а не ноль: «0 свободно» обещает то, чего мы не знаем.
 */
const FREE_QUANTITY: DerivedColumn = {
  name: 'free_quantity',
  after: 'reserved_quantity',
  from: ['quantity', 'reserved_quantity'],
  compute: (row) => {
    const quantity = row.quantity
    const reserved = row.reserved_quantity
    if (typeof quantity !== 'number' || typeof reserved !== 'number') return null
    return quantity - reserved
  },
  flagWhenNotPositive: 'stock.noFree',
}

export const SCREENS: Record<string, EntityScreen> = {
  'sales.deal': {
    quick: [
      mine('responsible_employee_id'),
      {
        key: 'open',
        labelKey: 'quick.open',
        columns: ['status'],
        build: () => [{ column: 'status', operator: 'eq', value: 'open' }],
      },
      {
        // Просрочена открытая сделка, у которой прошёл ожидаемый срок закрытия. Закрытая
        // с прошедшим сроком — история, а не проблема.
        key: 'overdue',
        labelKey: 'quick.overdue',
        columns: ['status', 'expected_close_on'],
        build: ({ today }) => [
          { column: 'status', operator: 'eq', value: 'open' },
          { column: 'expected_close_on', operator: 'lt', value: today },
        ],
      },
    ],
    search: 'title',
  },
  'sales.lead': {
    quick: [
      mine('responsible_employee_id'),
      {
        key: 'new',
        labelKey: 'quick.newLeads',
        columns: ['status'],
        build: () => [{ column: 'status', operator: 'eq', value: 'new' }],
      },
    ],
    search: 'title',
  },
  'sales.counterparty': {
    quick: [mine('responsible_employee_id')],
    search: 'name',
  },
  'billing.invoice': {
    quick: [
      mine('responsible_employee_id'),
      {
        // Не только статус `overdue`: его ставит обход дебиторки по расписанию, и между
        // сроком и обходом счёт уже просрочен, а статус ещё нет. Неоплаченный с прошедшим
        // сроком — то, что спрашивают.
        key: 'overdue',
        labelKey: 'quick.overdue',
        columns: ['status', 'due_on'],
        build: ({ today }) => [
          { column: 'status', operator: 'in', value: ['sent', 'partially_paid', 'overdue'] },
          { column: 'due_on', operator: 'lt', value: today },
        ],
      },
      {
        key: 'unpaid',
        labelKey: 'quick.unpaid',
        columns: ['status'],
        build: () => [
          { column: 'status', operator: 'in', value: ['sent', 'partially_paid', 'overdue'] },
        ],
      },
    ],
    search: 'number',
  },
  'warehouse.pick': {
    quick: [mine('assignee_employee_id')],
  },
  // Очередь обращений (#123). «Просрочено» — нет первого ответа и срок прошёл: те же две
  // колонки, по которым считает сервер (карточка, sla_breached), а не флаг, посчитанный здесь.
  'support.ticket': {
    quick: [
      {
        key: 'mine',
        labelKey: 'quick.mine',
        columns: ['assigned_user_id'],
        build: ({ userId }) =>
          userId ? [{ column: 'assigned_user_id', operator: 'eq', value: userId }] : null,
      },
      {
        key: 'waiting',
        labelKey: 'quick.ticketWaiting',
        columns: ['status'],
        build: () => [{ column: 'status', operator: 'eq', value: 'open' }],
      },
      {
        key: 'unassigned',
        labelKey: 'quick.ticketUnassigned',
        columns: ['assigned_user_id', 'status'],
        build: () => [
          { column: 'assigned_user_id', operator: 'is_null', value: null },
          { column: 'status', operator: 'in', value: ['open', 'answered'] },
        ],
      },
      {
        key: 'slaBreached',
        labelKey: 'quick.slaBreached',
        columns: ['first_answer_at', 'first_response_due_at', 'status'],
        build: () => [
          { column: 'status', operator: 'in', value: ['open', 'answered'] },
          { column: 'first_answer_at', operator: 'is_null', value: null },
          { column: 'first_response_due_at', operator: 'lt', value: new Date().toISOString() },
        ],
      },
      // AI triage (#132): the urgent mark set by a routing rule or a person, and the category
      // the model read — columns of the ticket, filtered by the server like any other.
      {
        key: 'urgent',
        labelKey: 'quick.ticketUrgent',
        columns: ['urgent', 'status'],
        build: () => [
          { column: 'urgent', operator: 'eq', value: true },
          { column: 'status', operator: 'in', value: ['open', 'answered'] },
        ],
      },
      {
        key: 'claims',
        labelKey: 'quick.ticketClaims',
        columns: ['triage_category'],
        build: () => [{ column: 'triage_category', operator: 'in', value: ['claim', 'return'] }],
      },
    ],
    search: 'subject',
    mono: ['number'],
  },
  'warehouse.stock': {
    quick: [
      {
        key: 'reserved',
        labelKey: 'quick.reserved',
        columns: ['reserved_quantity'],
        build: () => [{ column: 'reserved_quantity', operator: 'gt', value: 0 }],
      },
      {
        key: 'empty',
        labelKey: 'quick.zeroStock',
        columns: ['quantity'],
        build: () => [{ column: 'quantity', operator: 'eq', value: 0 }],
      },
    ],
    // Текстовой колонки у остатка нет (товар и ячейка — ссылки), а поиск по UUID никому не
    // нужен. Поиска нет, а не поиск, который ничего не находит.
    search: null,
    defaultGroupBy: 'warehouse_id',
    mono: ['cell_id'],
    strong: ['free_quantity'],
    derived: [FREE_QUANTITY],
  },
}

/** Колонки, по которым ищет список без своей настройки, — по порядку предпочтения. */
const SEARCH_FALLBACK = ['title', 'name', 'number', 'last_name', 'payer_name']

export function screenOf(entity: string): EntityScreen {
  return SCREENS[entity] ?? {}
}

function filterableColumn(descriptors: ColumnDescriptor[], name: string) {
  return descriptors.find((column) => column.name === name && column.filterable)
}

/** Чипы, которые сервер примет у этой сущности и этого пользователя. */
export function availableChips(
  entity: string,
  descriptors: ColumnDescriptor[],
  context: QuickContext,
): { chip: QuickFilter; filters: FilterSpec[] }[] {
  return (screenOf(entity).quick ?? []).flatMap((chip) => {
    if (!chip.columns.every((name) => filterableColumn(descriptors, name))) return []
    const filters = chip.build(context)
    return filters ? [{ chip, filters }] : []
  })
}

/** Колонка поиска по списку или null, если искать не по чему. */
export function searchColumn(entity: string, descriptors: ColumnDescriptor[]): string | null {
  const configured = screenOf(entity).search
  if (configured === null) return null
  const candidates = configured ? [configured] : SEARCH_FALLBACK
  return (
    candidates.find((name) => filterableColumn(descriptors, name)?.type === 'text') ?? null
  )
}

export function sameFilter(left: FilterSpec, right: FilterSpec): boolean {
  return (
    left.column === right.column &&
    left.operator === right.operator &&
    JSON.stringify(left.value ?? null) === JSON.stringify(right.value ?? null)
  )
}

/** Все ли условия чипа уже стоят в фильтрах списка. */
export function chipActive(chipFilters: FilterSpec[], filters: FilterSpec[]): boolean {
  return chipFilters.every((spec) => filters.some((candidate) => sameFilter(candidate, spec)))
}

/** Включает чип: добавляет его условия, которых ещё нет. */
export function withChip(chipFilters: FilterSpec[], filters: FilterSpec[]): FilterSpec[] {
  const missing = chipFilters.filter(
    (spec) => !filters.some((candidate) => sameFilter(candidate, spec)),
  )
  return [...filters, ...missing]
}

/**
 * Выключает чип: снимает его условия.
 *
 * Условие, которое держит и другой включённый чип, остаётся, — если тот чип шире
 * выключаемого: иначе снятие «Моих» молча расширило бы соседний чип с общим условием.
 * Чип, целиком лежащий внутри выключаемого («Открытые» внутри «Просроченных» — обе держат
 * `status = open`), уходит вместе с ним: отдельно его не включали, он горел потому, что
 * его условия оказались среди условий большего, и оставить его значило бы после снятия
 * «Просроченных» показать список, суженный тем, чего пользователь не выбирал.
 */
export function withoutChip(
  chipFilters: FilterSpec[],
  filters: FilterSpec[],
  otherActive: FilterSpec[][],
): FilterSpec[] {
  const holders = otherActive.filter((other) => !chipActive(other, chipFilters))
  return filters.filter(
    (candidate) =>
      !chipFilters.some((spec) => sameFilter(candidate, spec)) ||
      holders.some((other) => other.some((spec) => sameFilter(candidate, spec))),
  )
}

/** Сегодня `YYYY-MM-DD` по местным часам — не `toISOString`, который даёт дату UTC. */
export function localToday(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}
